import { Button } from 'primereact/button';
import {
  Attribute,
  AttributeWithUsers,
  AssignmentRule,
  SupplierDiscount,
} from 'services/bidAnalysis/bidAnalysisServices';
import { formatRuleConditions } from '../ReferenceTables.logic';
import classes from '../ReferenceTables.module.scss';

// ============================================================
// ATTRIBUTE TABLE TEMPLATES
// ============================================================

export const attributeNameBodyTemplate = (rowData: Attribute) => {
  return <span>{rowData.attribute_name}</span>;
};

export const attributeDescriptionBodyTemplate = (rowData: Attribute) => {
  return <span>{rowData.attribute_description}</span>;
};

export const createAttributeActionsBodyTemplate = (
  handleEditAttribute: (attr: Attribute) => void,
  handleDeleteAttribute: (attr: Attribute) => void
) => (rowData: Attribute) => {
  return (
    <div className={classes.actionButtons}>
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded p-button-plain"
        onClick={() => handleEditAttribute(rowData)}
        tooltip="Edit"
        tooltipOptions={{ position: 'top' }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded p-button-danger"
        onClick={() => handleDeleteAttribute(rowData)}
        tooltip="Delete"
        tooltipOptions={{ position: 'top' }}
      />
    </div>
  );
};

// ============================================================
// USER-ATTRIBUTE MAPPING TABLE TEMPLATES
// ============================================================

export const mappingAttributeNameBodyTemplate = (rowData: AttributeWithUsers) => {
  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{rowData.attribute_name}</div>
      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
        {rowData.attribute_description}
      </div>
    </div>
  );
};

export const userCountBodyTemplate = (rowData: AttributeWithUsers) => {
  return (
    <span style={{ fontWeight: 500 }}>
      {rowData.user_count} {rowData.user_count === 1 ? 'user' : 'users'}
    </span>
  );
};

export const usersListBodyTemplate = (rowData: AttributeWithUsers) => {
  if (rowData.users.length === 0) {
    return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No users assigned</span>;
  }

  return (
    <div className={classes.usersList}>
      {rowData.users.map(user => (
        <span key={user.mapping_id} className={classes.userBadge}>
          {user.user_details.name} ({user.user_details.email})
        </span>
      ))}
    </div>
  );
};

export const createMappingAttributeActionsBodyTemplate = (
  handleEditUsers: (attr: AttributeWithUsers) => void
) => (rowData: AttributeWithUsers) => {
  return (
    <div className={classes.actionButtons}>
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded p-button-plain"
        onClick={() => handleEditUsers(rowData)}
        tooltip="Edit Users"
        tooltipOptions={{ position: 'top' }}
      />
    </div>
  );
};

// ============================================================
// RULE TABLE TEMPLATES
// ============================================================

export const ruleNameBodyTemplate = (rowData: AssignmentRule) => {
  return <span>{rowData.rule_name}</span>;
};

export const contractTypeBodyTemplate = (rowData: AssignmentRule) => {
  return <span>{rowData.rule_json.contract_type}</span>;
};

export const conditionsBodyTemplate = (rowData: AssignmentRule) => {
  return <div style={{ fontSize: '0.875rem' }}>{formatRuleConditions(rowData.rule_json)}</div>;
};

export const ruleAttributesBodyTemplate = (rowData: AssignmentRule) => {
  if (rowData.attributes.length === 0) {
    return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No attributes mapped</span>;
  }

  return (
    <div className={classes.usersList}>
      {rowData.attributes.map(attr => (
        <span key={attr.id} className={classes.attributeBadge}>
          {attr.attribute_name}
        </span>
      ))}
    </div>
  );
};

export const createRuleActionsBodyTemplate = (
  handleEditRule: (rule: AssignmentRule) => void,
  handleDeleteRule: (rule: AssignmentRule) => void
) => (rowData: AssignmentRule) => {
  return (
    <div className={classes.actionButtons}>
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded p-button-plain"
        onClick={() => handleEditRule(rowData)}
        tooltip="Edit"
        tooltipOptions={{ position: 'top' }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded p-button-danger"
        onClick={() => handleDeleteRule(rowData)}
        tooltip="Delete"
        tooltipOptions={{ position: 'top' }}
      />
    </div>
  );
};

// ============================================================
// SUPPLIER DISCOUNT TABLE TEMPLATES
// ============================================================

const formatDecimal = (value: number | null | undefined): string => {
  if (value == null) return '—';
  // Display exactly as stored in database (no multiplication)
  return String(value);
};

export const supplierBodyTemplate = (rowData: SupplierDiscount) => {
  return <span>{rowData.supplier ?? '—'}</span>;
};

export const supplierNameBodyTemplate = (rowData: SupplierDiscount) => {
  return <span>{rowData.supplier_name ?? '—'}</span>;
};

export const supplierCodeBodyTemplate = (rowData: SupplierDiscount) => {
  return <span>{rowData.supplier_code ?? '—'}</span>;
};

export const promptPayDiscountBodyTemplate = (rowData: SupplierDiscount) => {
  return <span>{formatDecimal(rowData.prompt_pay_discount)}</span>;
};

export const adminFeeBodyTemplate = (rowData: SupplierDiscount) => {
  return <span>{formatDecimal(rowData.admin_fee)}</span>;
};

export const createSupplierDiscountActionsBodyTemplate = (
  handleEditSupplierDiscount: (discount: SupplierDiscount) => void,
  handleDeleteSupplierDiscount: (discount: SupplierDiscount) => void
) => (rowData: SupplierDiscount) => {
  return (
    <div className={classes.actionButtons}>
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded p-button-plain"
        onClick={() => handleEditSupplierDiscount(rowData)}
        tooltip="Edit"
        tooltipOptions={{ position: 'top' }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded p-button-danger"
        onClick={() => handleDeleteSupplierDiscount(rowData)}
        tooltip="Delete"
        tooltipOptions={{ position: 'top' }}
      />
    </div>
  );
};
